import React, { useEffect, useState } from 'react';
import { Typography, Box, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Grid, CircularProgress } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContextNavBar from '../components/ContextNavBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../axios';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';

function formatarDataBR(data) {
  if (!data) return '-';
  try {
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return data;
  }
}

const Relatorios = () => {
  const navigate = useNavigate();
  const [alunos, setAlunos] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [aulas, setAulas] = useState([]);
  const [formandos, setFormandos] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    async function fetchDados() {
      setLoading(true);
      setErro('');
      try {
        const [alunosRes, profsRes, turmasRes, aulasRes, formandosRes, matsRes, periodosRes] = await Promise.all([
          api.get('/alunos/'),
          api.get('/professores/'),
          api.get('/turmas/'),
          api.get('/aulas/'),
          api.get('/formandos/'),
          api.get('/matriculas/'),
          api.get('/periodos/')
        ]);
        setAlunos(alunosRes.data);
        setProfessores(profsRes.data);
        setTurmas(turmasRes.data);
        setAulas(aulasRes.data);
        setFormandos(formandosRes.data?.formandos || formandosRes.data || []);
        setMatriculas(matsRes.data);
        setPeriodos(periodosRes.data);
      } catch (e) {
        setErro('Erro ao carregar dados do servidor');
      }
      setLoading(false);
    }
    fetchDados();
  }, []);

  const alunosPorTurma = {};
  matriculas.forEach(m => {
    alunosPorTurma[m.turma_id] = (alunosPorTurma[m.turma_id] || 0) + 1;
  });

  const periodoMap = {};
  periodos.forEach(p => {
    periodoMap[p.id] = p.mes && p.ano ? `${p.mes}/${p.ano}` : `Período #${p.id}`;
  });

  const turmasRecentes = [...turmas].slice(-5).reverse();

  const aulasRecentes = [...aulas].sort((a, b) => new Date(b.data) - new Date(a.data)).slice(0, 10);

  const formandosRecentes = Array.isArray(formandos) ? formandos.slice(0, 10) : [];

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  if (erro) {
    return (
      <>
        <Header onLogout={handleLogout} />
        <ContextNavBar context="startfy" />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 1, md: 2 } }}>
          <Typography color="error">{erro}</Typography>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ArrowBackIcon
            sx={{ cursor: 'pointer', color: '#666' }}
            onClick={() => navigate('/')}
          />
          <Typography variant="h4" sx={{ fontSize: { xs: '1.3rem', md: '2rem' } }}>Relatórios</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                { label: 'Total de Alunos', value: alunos.length },
                { label: 'Total de Professores', value: professores.length },
                { label: 'Total de Turmas', value: turmas.length },
                { label: 'Total de Aulas', value: aulas.length },
              ].map(item => (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Card sx={{ bgcolor: DARK, color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" sx={{ color: LIME, fontWeight: 'bold' }}>
                        {item.value}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {item.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>Turmas mais recentes</Typography>
            <Paper sx={{ mb: 4 }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Nome</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Período</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">Qtd. Alunos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {turmasRecentes.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">Nenhuma turma encontrada.</TableCell></TableRow>
                    ) : (
                      turmasRecentes.map(turma => (
                        <TableRow key={turma.id}>
                          <TableCell>{turma.nome}</TableCell>
                          <TableCell>{periodoMap[turma.periodo_id] || '-'}</TableCell>
                          <TableCell align="center">{alunosPorTurma[turma.id] || 0}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>Últimas aulas</Typography>
            <Paper sx={{ mb: 4 }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Data</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Turma</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Professor</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Lição</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aulasRecentes.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center">Nenhuma aula encontrada.</TableCell></TableRow>
                    ) : (
                      aulasRecentes.map(aula => (
                        <TableRow key={aula.id}>
                          <TableCell>{formatarDataBR(aula.data)}</TableCell>
                          <TableCell>{aula.turma_nome || '-'}</TableCell>
                          <TableCell>{aula.professor_nome || '-'}</TableCell>
                          <TableCell>{aula.licao || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>Formandos recentes</Typography>
            <Paper>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Nome</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>E-mail</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Telefone</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Turma</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Conclusão</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formandosRecentes.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">Nenhum formando encontrado.</TableCell></TableRow>
                    ) : (
                      formandosRecentes.map(f => (
                        <TableRow key={f.id}>
                          <TableCell>{f.aluno_nome || '-'}</TableCell>
                          <TableCell>{f.aluno_email || '-'}</TableCell>
                          <TableCell>{f.aluno_telefone || '-'}</TableCell>
                          <TableCell>{f.turma_nome || '-'}</TableCell>
                          <TableCell>{formatarDataBR(f.data_conclusao)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default Relatorios;
